import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { Transformation } from './getResizedImageUrl';
import { getResizedImageUrl } from './getResizedImageUrl';

export { getResizedImageUrl };

export type { Transformation };

export type SrcSetEntry = {
  width: number;
  src: string;
};

export type SrcSet = SrcSetEntry[];

/**
 * Generic implementation of a responsive image.
 * Relies on the `ResizeObserver` instead of a plain `srcset` to achieve smaller downloads in dumb/desktop browsers.
 *
 * @param aspectRatio if set (recommended), enforces an aspect ratio on this component, even while it is loading or if the image file cannot be loaded. Improves render performance and reduces layout changes.
 * @param src a single URL or an array of image variants in different widths; in the latter case the component initializes with the smallest available size on the server and later selects as needed.
 */
export type ResponsiveImageProps = {
  alt: string;
  aspectRatio?: number;
  className?: string;
  loading?: 'eager' | 'lazy'; // should be HTMLImageElement['loading'], but TSC complains
  style?: CSSProperties;
  title?: string;
  width?: CSSProperties['width'];
  src: string | SrcSet;
};

export const ResponsiveImage: FC<ResponsiveImageProps> = ({
  alt,
  aspectRatio,
  className,
  loading,
  src,
  style,
  title,
  width = '100%',
}) => {
  const [dpr, setDpr] = useState(1);
  const [currentWidth, setCurrentWidth] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const observer = useRef<ResizeObserver | null>(null);
  const imageElement = useRef<HTMLImageElement | null>(null);

  const memoizedStyle = useMemo<CSSProperties>(
    () => ({
      aspectRatio:
        typeof aspectRatio === 'number' ? `${aspectRatio} / 1` : undefined,
      filter: `blur(${isLoading ? 16 : 0}px) grayscale(${isLoading ? 1 : 0})`,
      transition: 'filter .5s linear',
      visibility: hasError ? 'hidden' : undefined,
      ...style,
    }),
    [aspectRatio, isLoading, hasError, style]
  );

  const memoizedSrc = useMemo(() => {
    if (typeof src === 'string') {
      return src;
    }

    const dprWidth = Math.round(currentWidth * dpr);

    const srcSetEntry = src.reduce<SrcSetEntry>((prevSrc, currSrc) => {
      const prevDiff = prevSrc.width - dprWidth;
      const currDiff = currSrc.width - dprWidth;

      if (prevDiff < 0) {
        // Previous image is too small; if current image is bigger, use it
        return currDiff > prevDiff ? currSrc : prevSrc;
      } else {
        // Previous is large enough; if current image is also large enough but smaller, use it
        return currDiff >= 0 && currDiff < prevDiff ? currSrc : prevSrc;
      }
    }, src[0]);

    return srcSetEntry.src;
  }, [currentWidth, src, dpr]);

  const [actualSrc, setActualSrc] = useState(memoizedSrc);

  useEffect(() => {
    // only blur (loading state) the image when the src was really changed
    // cos useMemo could return same values and it causes unexpected/extra blurring
    if (memoizedSrc !== actualSrc) {
      setActualSrc(memoizedSrc);
      setIsLoading(true);
    }
  }, [actualSrc, memoizedSrc]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setHasError(false);
    setIsLoading(false);
  }, []);

  const handleRef = useCallback((ref: HTMLImageElement | null) => {
    if (observer.current && imageElement.current) {
      observer.current.unobserve(imageElement.current);
    }

    if (ref) {
      // Update width and DPR on client-side mount
      setCurrentWidth(ref.width);
      setDpr(window.devicePixelRatio);

      const handleResize: ResizeObserverCallback = (
        entries: ResizeObserverEntry[]
      ) => {
        setCurrentWidth(entries[0]?.contentRect?.width || 0);
      };

      observer.current = new ResizeObserver(handleResize);
      observer.current.observe(ref);
    }

    imageElement.current = ref;
  }, []);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={alt}
      className={className}
      loading={loading}
      onError={handleError}
      onLoad={handleSuccess}
      ref={handleRef}
      src={actualSrc}
      style={memoizedStyle}
      title={title}
      width={width}
    />
  );
};
